import React, { ReactNode, useEffect, useState } from "react";
import { Flex, Text } from "@fluentui/react-northstar";
import TableHeader from "./tableHeader/TableHeader";
import pl from "shared/messages/pl";
import {
    numberCellComparator,
    stringCellComparator,
    TableColumnDefinition,
} from "shared/reports/components/AdvancedTable";
import ReportsTable, { ReportsTableProps } from "shared/reports/components/reportsTable/ReportsTable";
import _ from "lodash";
import { IReportElement, IReportEntity, ITenantTableColumn } from "shared/reports/models/models";

interface EntitiesReportTableProps {
    reportsEntities: IReportEntity[];
    tenantTableColumns: ITenantTableColumn[];
    renderReportElement: (report: IReportElement | undefined) => ReactNode;
}

const EntitiesReportTable: React.FC<EntitiesReportTableProps> = (props: EntitiesReportTableProps) => {
    const [reportsTableProps, setReportsTableProps] = useState<ReportsTableProps>();

    const prepareRows = (tableColumnDefinitions: TableColumnDefinition[]) => {
        return props.reportsEntities.map((reportEntity, index) => {
            return {
                key: `row_${index}`,
                items: getRowItems(reportEntity, tableColumnDefinitions),
            };
        });
    };

    const getRowItems = (reportEntity: IReportEntity, tableColumnDefinitions: TableColumnDefinition[]) => {
        let items = [];
        items.push({
            content: (
                <Flex column fill>
                    <Text weight="semibold" size="small">
                        {reportEntity.userFullName}
                    </Text>
                    {reportEntity.branchName && <Text size="small">{reportEntity.branchName}</Text>}
                </Flex>
            ),
            key: reportEntity.userFullName,
            sortvalue: reportEntity.userFullName,
        });

        items = items.concat(createCells(reportEntity, tableColumnDefinitions));
        return items;
    };

    const createCells = (reportEntity: IReportEntity, tableColumnDefinitions: TableColumnDefinition[]): any[] => {
        const result: any[] = [];
        tableColumnDefinitions.forEach((columnDefinition) => {
            const reportElement = reportEntity.elements?.find((i) => i.columnKey === columnDefinition.key);

            result.push({
                content: props.renderReportElement(reportElement),
                key: columnDefinition.key,
                sortvalue: reportElement?.sortValue,
            });
        });
        return result;
    };

    const prepareColumnDefinitions = (): TableColumnDefinition[] => {
        const result: TableColumnDefinition[] = [];

        props.tenantTableColumns.forEach((tenantTableColumns) => {
            tenantTableColumns.tableColumns.forEach((column, idx) => {
                let headerTopContent = <></>;

                if (idx === 0) {
                    headerTopContent = (
                        <TableHeader text={tenantTableColumns.tenant.displayName} size="medium" weight="semibold" />
                    );
                }

                result.push({
                    key: column.columnKey,
                    name: column.columnKey,
                    title: column.displayName,
                    cellComparator: numberCellComparator,
                    headerTop: <div>{headerTopContent}</div>,
                });
            });
        });
        return result;
    };

    const userNameColumn = {
        key: pl.reports.reportTable.nameHeader,
        name: pl.reports.reportTable.nameHeader,
        title: pl.reports.reportTable.nameHeader,
        cellComparator: stringCellComparator,
        headerTop: <div />,
    };

    const prepareTable = (): ReportsTableProps => {
        let tableColumns: TableColumnDefinition[] = [];
        tableColumns.push(userNameColumn);
        const columnDefinitions = prepareColumnDefinitions();
        tableColumns = tableColumns.concat(columnDefinitions);
        const rows = prepareRows(columnDefinitions);

        return { columnDefinitions: tableColumns, rows: rows };
    };

    useEffect(() => {
        setReportsTableProps(prepareTable());
    }, props.reportsEntities);

    if (reportsTableProps) {
        return <ReportsTable rows={reportsTableProps.rows} columnDefinitions={reportsTableProps.columnDefinitions} />;
    }
    return <></>;
};

export default EntitiesReportTable;
